import { useState } from "react";
import api from "../../../utils/axiosInstance";
import { toast } from "../../../components/Toast/toast";

const managerName = (m) =>
  `${m.first_name || ""} ${m.last_name || ""}`.trim() || `Manager #${m.id}`;

const managerContact = (m) => m.auth?.email || m.email || m.phone || "—";

const ManagersCard = ({
  pool,
  allManagers,
  isEditing,
  setIsEditing,
  formState,
  setFormState,
  setPool,
}) => {
  const [saving, setSaving] = useState(false);

  const handleMultiSelect = (field, id) => {
    setFormState((prev) => {
      const values = prev[field];
      return {
        ...prev,
        [field]: values.includes(id)
          ? values.filter((v) => v !== id)
          : [...values, id],
      };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        manager_ids: formState.manager_ids,
      };

      const res = await api.put(`/client-pools/${pool.id}`, payload);
      if (res.data.success) {
        const updatedManagers = res.data.data.managers;

        setFormState((prev) => ({
          ...prev,
          manager_ids: updatedManagers.map((m) => m.id),
        }));

        setPool((prev) => ({
          ...prev,
          managers: updatedManagers,
        }));

        setIsEditing(false);
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to update managers");
    } finally {
      setSaving(false);
    }
  };

  const HEADERS_VIEW = ["Name", "Role", "Contact"];
  const HEADERS_EDIT = ["Select", "Name", "Role", "Contact"];
  const headers = isEditing ? HEADERS_EDIT : HEADERS_VIEW;

  return (
    <section>
      <div className="flex justify-between items-center mb-3">
        <div className="flex items-center gap-2">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
            Managers
          </h3>
          <span className="text-[10px] font-semibold text-gray-500">
            ({pool.managers?.length || 0})
          </span>
        </div>
        {!isEditing ? (
          <button
            onClick={() => setIsEditing(true)}
            className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded"
          >
            Update
          </button>
        ) : (
          <div className="flex gap-2 text-sm">
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-3 py-1.5 font-medium text-white bg-blue-600 hover:bg-blue-700 rounded disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? "Saving..." : "Save"}
            </button>
            <button
              onClick={() => setIsEditing(false)}
              disabled={saving}
              className="px-3 py-1.5 font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        )}
      </div>

      <div className="w-full bg-white shadow rounded-lg overflow-hidden">
        <div className="h-[250px] overflow-y-auto overflow-x-auto">
          <table className="w-full table-auto border-collapse">
            <thead className="sticky top-0 z-10">
              <tr className="bg-white border-b text-[10px] tracking-widest text-gray-500 uppercase">
                {headers.map((h) => (
                  <th
                    key={h}
                    className="px-3 py-2 text-center font-semibold whitespace-nowrap"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isEditing ? (
                allManagers.length === 0 ? (
                  <tr>
                    <td
                      colSpan={headers.length}
                      className="py-10 text-center text-gray-400 text-xs"
                    >
                      No managers available.
                    </td>
                  </tr>
                ) : (
                  allManagers.map((m, index) => {
                    const isChecked = formState.manager_ids.includes(m.id);
                    const rowBg = isChecked
                      ? "bg-blue-50"
                      : index % 2 === 0
                      ? "bg-gray-50"
                      : "bg-white";
                    return (
                      <tr
                        key={m.id}
                        onClick={() => handleMultiSelect("manager_ids", m.id)}
                        className={`${rowBg} hover:bg-blue-50 transition-colors cursor-pointer`}
                      >
                        <td className="px-3 py-2 text-center">
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => handleMultiSelect("manager_ids", m.id)}
                            className="w-3.5 h-3.5 accent-blue-600 cursor-pointer"
                            onClick={(e) => e.stopPropagation()}
                          />
                        </td>
                        <td className="px-3 py-2 text-xs whitespace-nowrap text-center text-gray-900">
                          {managerName(m)}
                        </td>
                        <td className="px-3 py-2 text-xs whitespace-nowrap text-center text-gray-700">
                          {m.role?.role_details?.name || "—"}
                        </td>
                        <td className="px-3 py-2 text-xs whitespace-nowrap text-center text-gray-700">
                          {managerContact(m)}
                        </td>
                      </tr>
                    );
                  })
                )
              ) : pool.managers && pool.managers.length > 0 ? (
                pool.managers.map((m, index) => {
                  const rowBg = index % 2 === 0 ? "bg-gray-50" : "bg-white";
                  return (
                    <tr
                      key={m.id}
                      className={`${rowBg} hover:bg-blue-50 transition-colors`}
                    >
                      <td className="px-3 py-2 text-xs whitespace-nowrap text-center text-gray-900">
                        {managerName(m)}
                      </td>
                      <td className="px-3 py-2 text-xs whitespace-nowrap text-center text-gray-700">
                        {m.role?.role_details?.name || "—"}
                      </td>
                      <td className="px-3 py-2 text-xs whitespace-nowrap text-center text-gray-700">
                        {managerContact(m)}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td
                    colSpan={headers.length}
                    className="py-10 text-center text-gray-400 text-xs"
                  >
                    No managers assigned.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
};

export default ManagersCard;
